import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import {getArtifactTypeLabel} from 'llm/chat-library/ArtifactTypes';
import {ChatContext} from 'llm/chat-library/ChatContext';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {GRAY, GRAY_LIGHT, hexToRgba} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';

import type {ChatLayoutProps} from './ChatLayoutRegistry';
import {TabbedViewerPane} from './TabbedViewerPane';
import {useTabbedViewer} from './useTabbedViewer';

const ARTIFACT_TAB_ID = '__artifact__';

/**
 * Standard two-panel chat layout.
 * Displays chat on the left and a tabbed viewer pane (artifact + file tabs) on the right.
 */
export function SideBySideLayout({chatContent, artifacts, isEditable}: ChatLayoutProps): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const {primaryArtifact} = artifacts;
  const {tabs, activeTab, openTab, closeTab, setActiveTab} = useTabbedViewer();

  useOnMount(() => {
    SidepanelActions.expand();
  });

  useEffect(() => {
    if (primaryArtifact != null) {
      openTab(
        {
          kind: 'artifact',
          id: ARTIFACT_TAB_ID,
          label: getArtifactTypeLabel(primaryArtifact.artifactType),
          Component: primaryArtifact.Component,
          data: primaryArtifact.data,
          editable: isEditable,
        },
        {position: 'start'}
      );
    } else {
      closeTab(ARTIFACT_TAB_ID);
    }
  }, [primaryArtifact, isEditable, openTab, closeTab]);

  const handleOpenFile = useCallback(
    (chatSessionId: string, externalFileId: string, filename: string, mimeType: string) => {
      openTab({
        kind: 'file',
        id: externalFileId,
        chatSessionId,
        externalFileId,
        filename,
        mimeType,
        label: filename,
      });
    },
    [openTab]
  );

  const contextValue = useMemo(() => ({openFile: handleOpenFile}), [handleOpenFile]);

  if (tabs.length === 0) {
    return <ChatContext.Provider value={contextValue}>{chatContent}</ChatContext.Provider>;
  }

  return (
    <ChatContext.Provider value={contextValue}>
      <div
        className={css(styles.sideBySideContainer)}
        {...getTestProps({component: 'SideBySideLayout', element: 'root'})}
      >
        <PanelGroup direction="horizontal" style={{width: '100%', height: '100%'}}>
          <Panel defaultSize={33} minSize={20} maxSize={80} className={css(styles.chatPanel)}>
            <div
              {...getTestProps({component: 'SideBySideLayout', element: 'chat-panel'})}
              className={css(styles.panelWrapper)}
            >
              <div
                className={css(styles.scrollableContent)}
                {...getTestProps({component: 'SideBySideLayout', element: 'scrollable-content'})}
              >
                {chatContent}
              </div>
            </div>
          </Panel>

          <PanelResizeHandle
            className={css(styles.resizer, isDragging && styles.resizerActive)}
            onDragging={setIsDragging}
          >
            <div
              {...getTestProps({component: 'SideBySideLayout', element: 'resizer'})}
              className={css(styles.resizerWrapper)}
            >
              <div
                className={css(styles.resizerHandle)}
                {...getTestProps({component: 'SideBySideLayout', element: 'resizer-handle'})}
              />
            </div>
          </PanelResizeHandle>

          <Panel defaultSize={67} minSize={20} maxSize={80} className={css(styles.artifactPanel)}>
            <div
              {...getTestProps({component: 'SideBySideLayout', element: 'artifact-panel'})}
              className={css(styles.panelWrapper)}
            >
              <TabbedViewerPane
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onTabClose={closeTab}
              />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </ChatContext.Provider>
  );
}

const styles = StyleSheet.create({
  sideBySideContainer: {
    display: 'flex',
    minHeight: 0,
    flex: 1,
    position: 'relative',
    width: '100%',
  },
  chatPanel: {
    height: '100%',
  },
  artifactPanel: {
    height: '100%',
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'white',
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
    boxShadow: `2px 0 8px 0 ${GRAY}, inset -1px 0 0 0 ${GRAY_LIGHT}`,
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
});
